import React from 'react';

// ─── Helpers ──────────────────────────────────────────────────────────────────

// knockout_round_num represents teams remaining at that stage:
//   2 = Final, 4 = Semi Finals, 8 = Quarter Finals, 16 = Round of 16, etc.
function getRoundLabel(roundNum) {
  if (roundNum === 2)  return 'Final';
  if (roundNum === 4)  return 'Semi Finals';
  if (roundNum === 8)  return 'Quarter Finals';
  if (roundNum === 16) return 'Round of 16';
  if (roundNum === 32) return 'Round of 32';
  return `Round of ${roundNum}`;
}

function getStatusColor(status) {
  if (status === 'completed') return '#27ae60';
  if (status === 'active')    return '#e74c3c';
  return '#bdc3c7';
}

// ─── Single match card in the bracket ─────────────────────────────────────────

function BracketMatchCard({ match, teamMap, isFinal, navigate }) {
  const t1 = teamMap[match.team1_id];
  const t2 = teamMap[match.team2_id];
  const done    = match.status === 'completed';
  const live    = match.status === 'active';
  const isBye   = match.is_bye;

  let sets = [];
  if (done && match.set_scores) {
    try { sets = JSON.parse(match.set_scores); } catch {}
  }
  const hasSets = sets.length > 0;
  const t1Sets = hasSets ? (match.team1_sets ?? sets.filter(s => Number(s.t1) > Number(s.t2)).length) : null;
  const t2Sets = hasSets ? (match.team2_sets ?? sets.filter(s => Number(s.t2) > Number(s.t1)).length) : null;

  const cardStyle = {
    background: isFinal ? 'linear-gradient(135deg,#1a1a2e,#16213e)' : '#fff',
    border: live
      ? '2px solid #e74c3c'
      : done
        ? `2px solid #27ae60`
        : '1.5px solid #e8ecef',
    borderRadius: '10px',
    padding: '10px 12px',
    minWidth: '200px',
    maxWidth: '220px',
    boxShadow: live ? '0 0 12px rgba(231,76,60,0.25)' : '0 2px 8px rgba(0,0,0,0.06)',
    cursor: (done || live) ? 'pointer' : 'default',
    position: 'relative',
    transition: 'box-shadow 0.2s',
  };

  const handleClick = () => {
    if ((done || live) && navigate) navigate(`/matches/${match.id}/live`);
  };

  const teamRow = (team, isWinner, score, isTop) => (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '8px',
      padding: '5px 0',
      borderBottom: isTop ? `1px solid ${isFinal ? 'rgba(255,255,255,0.1)' : '#f0f4f8'}` : 'none',
    }}>
      <div style={{
        flex: 1, fontSize: '13px', fontWeight: isWinner ? '800' : '500',
        color: isFinal
          ? (team ? '#fff' : 'rgba(255,255,255,0.3)')
          : (team ? (isWinner ? '#1a1a2e' : '#555') : '#bdc3c7'),
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {isWinner && done && '🏆 '}
        {team?.name || (isBye ? 'BYE' : 'TBD')}
      </div>
      {done && (
        <div style={{
          fontSize: '15px', fontWeight: '800', minWidth: '24px', textAlign: 'right',
          color: isWinner ? '#27ae60' : (isFinal ? 'rgba(255,255,255,0.5)' : '#999'),
        }}>
          {score ?? '–'}
        </div>
      )}
    </div>
  );

  return (
    <div style={cardStyle} onClick={handleClick} title={done || live ? 'Click to view match' : ''}>
      {live && (
        <div style={{
          position: 'absolute', top: '-8px', right: '8px',
          background: '#e74c3c', color: '#fff', fontSize: '9px', fontWeight: '800',
          padding: '2px 6px', borderRadius: '6px', letterSpacing: '0.5px',
        }}>LIVE</div>
      )}
      {teamRow(t1, match.winner_team_id === match.team1_id, match.team1_score, true)}
      {teamRow(t2, match.winner_team_id === match.team2_id, match.team2_score, false)}
      {done && hasSets && (
        <div style={{ marginTop: '5px', borderTop: `1px solid ${isFinal ? 'rgba(255,255,255,0.1)' : '#f0f4f8'}`, paddingTop: '5px' }}>
          <div style={{ display: 'flex', gap: '3px', flexWrap: 'wrap', justifyContent: 'center', marginBottom: '2px' }}>
            {sets.map((s, i) => {
              const s1 = Number(s.t1), s2 = Number(s.t2);
              const sw = s1 > s2 ? 1 : s2 > s1 ? 2 : 0;
              return (
                <span key={i} style={{ fontSize: '10px', whiteSpace: 'nowrap' }}>
                  <span style={{ color: sw === 1 ? '#27ae60' : '#e74c3c', fontWeight: '700' }}>{s.t1}</span>
                  <span style={{ color: isFinal ? 'rgba(255,255,255,0.3)' : '#bdc3c7', margin: '0 1px' }}>-</span>
                  <span style={{ color: sw === 2 ? '#27ae60' : '#e74c3c', fontWeight: '700' }}>{s.t2}</span>
                  {i < sets.length - 1 && <span style={{ color: isFinal ? 'rgba(255,255,255,0.2)' : '#ccc', marginLeft: '2px' }}>,</span>}
                </span>
              );
            })}
          </div>
          <div style={{ textAlign: 'center', fontSize: '10px', color: isFinal ? 'rgba(255,255,255,0.4)' : '#7f8c8d', fontWeight: '600' }}>
            <span style={{ color: match.winner_team_id === match.team1_id ? '#27ae60' : (isFinal ? 'rgba(255,255,255,0.55)' : '#555') }}>{t1Sets}</span>
            <span style={{ color: isFinal ? 'rgba(255,255,255,0.2)' : '#bdc3c7', margin: '0 2px' }}>–</span>
            <span style={{ color: match.winner_team_id === match.team2_id ? '#27ae60' : (isFinal ? 'rgba(255,255,255,0.55)' : '#555') }}>{t2Sets}</span>
            {' sets'}
          </div>
        </div>
      )}
      {!done && !live && (
        <div style={{ fontSize: '10px', color: '#bdc3c7', textAlign: 'right', marginTop: '4px' }}>
          {match.scheduled_time
            ? `📅 ${new Date(match.scheduled_time).toLocaleString([], { month:'short', day:'numeric', hour:'2-digit', minute:'2-digit' })}`
            : 'Not scheduled'}
          {match.court_number ? ` · Court ${match.court_number}` : ''}
        </div>
      )}
    </div>
  );
}

// ─── SVG connector between two adjacent rounds ─────────────────────────────────

function RoundConnector({ fromCount, toCount, slotHeight, headerH }) {
  const totalH = fromCount * slotHeight;
  const w = 40;
  const paths = [];

  for (let i = 0; i < toCount; i++) {
    const topIdx    = i * 2;
    const bottomIdx = i * 2 + 1;

    const y1 = headerH + slotHeight * topIdx    + slotHeight / 2;
    const y2 = headerH + slotHeight * bottomIdx + slotHeight / 2;
    const yMid = (y1 + y2) / 2;

    paths.push(
      `M ${w} ${y1} H ${w / 2} V ${y2} M ${w / 2} ${y2} H ${w} M ${w / 2} ${yMid} H 0`
    );
  }

  return (
    <svg
      width={w}
      height={totalH + headerH}
      style={{ flexShrink: 0, display: 'block' }}
    >
      {paths.map((d, i) => (
        <path key={i} d={d} fill="none" stroke="#d0d7de" strokeWidth={1.5} />
      ))}
    </svg>
  );
}

// ─── Main exported component ──────────────────────────────────────────────────

export default function KnockoutBracket({ matches, teamMap = {}, navigate }) {
  if (!matches || matches.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '40px', color: '#7f8c8d' }}>
        No knockout matches yet.
      </div>
    );
  }

  // Group matches by knockout_round_num (descending = first round first)
  const roundMap = {};
  matches.forEach(m => {
    const rn = m.knockout_round_num || 2;
    if (!roundMap[rn]) roundMap[rn] = [];
    roundMap[rn].push(m);
  });

  // Sort round numbers descending (largest = first round, 2 = final)
  const roundNums = Object.keys(roundMap).map(Number).sort((a, b) => b - a);
  const numRounds = roundNums.length;

  // Sort matches within each round by position
  roundNums.forEach(rn => {
    roundMap[rn].sort((a, b) => (a.knockout_position || 0) - (b.knockout_position || 0));
  });

  const maxMatchesInFirstRound = roundMap[roundNums[0]].length;
  const SLOT_H   = 110;
  const HEADER_H = 40;
  const totalH   = maxMatchesInFirstRound * SLOT_H + HEADER_H;

  return (
    <div style={{ overflowX: 'auto', overflowY: 'visible', paddingBottom: '16px' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', minWidth: `${numRounds * 260 + (numRounds - 1) * 40}px` }}>
        {roundNums.map((roundNum, colIdx) => {
          const roundMatches = roundMap[roundNum];
          const count        = roundMatches.length;
          const isFinal      = roundNum === 2;   // Final = 2 teams remaining
          const label        = getRoundLabel(roundNum);

          const slotsPerMatch  = maxMatchesInFirstRound / count;
          const effectiveSlotH = slotsPerMatch * SLOT_H;

          return (
            <React.Fragment key={roundNum}>
              {colIdx > 0 && (() => {
                const prevCount = roundMap[roundNums[colIdx - 1]].length;
                return (
                  <RoundConnector
                    fromCount={prevCount}
                    toCount={count}
                    slotHeight={slotsPerMatch * 2 * SLOT_H}
                    headerH={HEADER_H}
                  />
                );
              })()}

              <div style={{ flexShrink: 0, width: '220px' }}>
                <div style={{
                  height: `${HEADER_H}px`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '11px', fontWeight: '800', textTransform: 'uppercase',
                  letterSpacing: '0.6px',
                  color: isFinal ? '#f39c12' : '#27ae60',
                  borderBottom: `2px solid ${isFinal ? '#f39c12' : '#27ae60'}`,
                }}>
                  {label}
                </div>

                {roundMatches.map((match) => (
                  <div
                    key={match.id}
                    style={{
                      height: `${effectiveSlotH}px`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '0 4px',
                    }}
                  >
                    <BracketMatchCard
                      match={match}
                      teamMap={teamMap}
                      isFinal={isFinal}
                      navigate={navigate}
                    />
                  </div>
                ))}
              </div>
            </React.Fragment>
          );
        })}

        {/* Champion display — final has knockout_round_num === 2 */}
        {(() => {
          const finalMatch = roundMap[2]?.[0];
          const champion   = finalMatch?.winner_team_id ? teamMap[finalMatch.winner_team_id] : null;
          return champion ? (
            <>
              <div style={{ width: '40px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width={40} height={totalH} style={{ display: 'block' }}>
                  <line x1={40} y1={totalH / 2} x2={0} y2={totalH / 2} stroke="#f39c12" strokeWidth={2} strokeDasharray="4 3" />
                </svg>
              </div>
              <div style={{
                flexShrink: 0, display: 'flex', alignItems: 'center',
                background: 'linear-gradient(135deg,#f39c12,#e67e22)',
                borderRadius: '12px', padding: '12px 20px',
                boxShadow: '0 4px 20px rgba(243,156,18,0.35)',
                alignSelf: 'center',
              }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', marginBottom: '4px' }}>🏆</div>
                  <div style={{ fontSize: '11px', fontWeight: '700', color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Champion</div>
                  <div style={{ fontSize: '15px', fontWeight: '900', color: '#fff', marginTop: '2px' }}>{champion.name}</div>
                </div>
              </div>
            </>
          ) : null;
        })()}
      </div>
    </div>
  );
}
